import React, { useState } from "react";
import {
  Plus,
  Pencil,
  Trash,
  Bank,
  PiggyBank,
  ChartLine,
  Coins,
  TrendUp,
  Wallet,
  CreditCard,
  House,
  GraduationCap,
  Car,
  HandCoins,
  TrendDown,
} from "@phosphor-icons/react";
import { useData } from "../context/DataContext";
import { Asset, Debt } from "../types";
import { formatCurrency } from "../utils/calculations";
import DollarInput from "../components/DollarInput";
import { useModal } from "../hooks/useModal";

const Assets: React.FC = () => {
  const { getDashboardData, dispatch, getCurrentUser } = useData();
  const dashboardData = getDashboardData();
  const [showAddModal, setShowAddModal] = useState(false);
  const [modalType, setModalType] = useState<"asset" | "debt">("asset");
  const [editingAsset, setEditingAsset] = useState<Asset | null>(null);
  const [editingDebt, setEditingDebt] = useState<Debt | null>(null);
  const [assetFormData, setAssetFormData] = useState({
    name: "",
    type: "savings" as Asset["type"],
    balance: 0,
    monthlyContribution: 0,
    interestRate: 0,
    isLiquid: true,
  });
  const [debtFormData, setDebtFormData] = useState({
    name: "",
    type: "credit-card" as Debt["type"],
    balance: 0,
    monthlyPayment: 0,
    interestRate: 0,
    isActive: true,
  });

  // Move useModal hook to top level
  const modalRef = useModal({
    isOpen: showAddModal,
    onClose: () => setShowAddModal(false),
  });

  const handleAssetFieldChange = (field: string, value: any) => {
    setAssetFormData({ ...assetFormData, [field]: value });
  };

  const handleDebtFieldChange = (field: string, value: any) => {
    setDebtFormData({ ...debtFormData, [field]: value });
  };

  const formatPercentageValue = (value: number): string => {
    if (value === 0) return "";
    return value.toString();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const now = new Date();

    const currentUser = getCurrentUser();
    if (!currentUser) {
      console.error("No current user found");
      return;
    }

    if (modalType === "asset") {
      const asset: Asset = {
        id: editingAsset?.id || `asset-${Date.now()}`,
        userId: currentUser.id,
        name: assetFormData.name,
        type: assetFormData.type,
        balance: assetFormData.balance,
        monthlyContribution: assetFormData.monthlyContribution || undefined,
        interestRate: assetFormData.interestRate || undefined,
        isLiquid: assetFormData.isLiquid,
        createdAt: now.toISOString(),
      };

      if (editingAsset) {
        dispatch({ type: "UPDATE_ASSET", payload: asset });
      } else {
        dispatch({ type: "ADD_ASSET", payload: asset });
      }
    } else {
      const debt: Debt = {
        id: editingDebt?.id || `debt-${Date.now()}`,
        userId: currentUser.id,
        name: debtFormData.name,
        type: debtFormData.type,
        balance: debtFormData.balance,
        monthlyPayment: debtFormData.monthlyPayment,
        interestRate: debtFormData.interestRate,
        isActive: debtFormData.isActive,
        createdAt: now.toISOString(),
      };

      if (editingDebt) {
        dispatch({ type: "UPDATE_DEBT", payload: debt });
      } else {
        dispatch({ type: "ADD_DEBT", payload: debt });
      }
    }

    setShowAddModal(false);
    setEditingAsset(null);
    setEditingDebt(null);
    setAssetFormData({
      name: "",
      type: "savings",
      balance: 0,
      monthlyContribution: 0,
      interestRate: 0,
      isLiquid: true,
    });
    setDebtFormData({
      name: "",
      type: "credit-card",
      balance: 0,
      monthlyPayment: 0,
      interestRate: 0,
      isActive: true,
    });
  };

  const handleEdit = (item: Asset | Debt, type: "asset" | "debt") => {
    if (type === "asset") {
      const asset = item as Asset;
      setEditingAsset(asset);
      setAssetFormData({
        name: asset.name,
        type: asset.type,
        balance: asset.balance,
        monthlyContribution: asset.monthlyContribution || 0,
        interestRate: asset.interestRate || 0,
        isLiquid: asset.isLiquid,
      });
    } else {
      const debt = item as Debt;
      setEditingDebt(debt);
      setDebtFormData({
        name: debt.name,
        type: debt.type,
        balance: debt.balance,
        monthlyPayment: debt.monthlyPayment,
        interestRate: debt.interestRate,
        isActive: debt.isActive,
      });
    }
    setModalType(type);
    setShowAddModal(true);
  };

  const handleDelete = (id: string, type: "asset" | "debt") => {
    const itemType = type === "asset" ? "asset" : "debt";
    if (window.confirm(`Are you sure you want to delete this ${itemType}?`)) {
      if (type === "asset") {
        dispatch({ type: "DELETE_ASSET", payload: id });
      } else {
        dispatch({ type: "DELETE_DEBT", payload: id });
      }
    }
  };

  const getAssetIcon = (type: Asset["type"]) => {
    switch (type) {
      case "savings":
        return (
          <PiggyBank className="w-5 h-5 text-green-500" weight="duotone" />
        );
      case "checking":
        return <Bank className="w-5 h-5 text-blue-500" weight="duotone" />;
      case "investment":
        return (
          <ChartLine className="w-5 h-5 text-yellow-500" weight="duotone" />
        );
      case "retirement":
        return <Coins className="w-5 h-5 text-purple-500" weight="duotone" />;
      case "ira":
        return <Coins className="w-5 h-5 text-purple-500" weight="duotone" />;
      case "portfolio":
        return (
          <ChartLine className="w-5 h-5 text-yellow-500" weight="duotone" />
        );
      case "crypto":
        return <TrendUp className="w-5 h-5 text-orange-500" weight="duotone" />;
      case "real-estate":
        return <House className="w-5 h-5 text-indigo-500" weight="duotone" />;
      case "vehicle":
        return <Car className="w-5 h-5 text-red-500" weight="duotone" />;
      case "other":
        return <Wallet className="w-5 h-5 text-gray-500" weight="duotone" />;
      default:
        return <Wallet className="w-5 h-5 text-gray-500" weight="duotone" />;
    }
  };

  const getDebtIcon = (type: Debt["type"]) => {
    switch (type) {
      case "credit-card":
        return <CreditCard className="w-5 h-5 text-red-500" weight="duotone" />;
      case "student-loan":
        return (
          <GraduationCap className="w-5 h-5 text-blue-500" weight="duotone" />
        );
      case "mortgage":
        return <House className="w-5 h-5 text-green-500" weight="duotone" />;
      case "car-loan":
        return <Car className="w-5 h-5 text-orange-500" weight="duotone" />;
      case "personal-loan":
        return (
          <HandCoins className="w-5 h-5 text-purple-500" weight="duotone" />
        );
      case "other":
        return (
          <CreditCard className="w-5 h-5 text-gray-500" weight="duotone" />
        );
      default:
        return (
          <CreditCard className="w-5 h-5 text-gray-500" weight="duotone" />
        );
    }
  };

  const getAssetTypeLabel = (type: Asset["type"]) => {
    switch (type) {
      case "savings":
        return "Savings Account";
      case "checking":
        return "Checking Account";
      case "investment":
        return "Investment Account";
      case "retirement":
        return "Retirement Account";
      case "ira":
        return "IRA";
      case "portfolio":
        return "Portfolio";
      case "crypto":
        return "Cryptocurrency";
      case "real-estate":
        return "Real Estate";
      case "vehicle":
        return "Vehicle";
      case "other":
        return "Other Asset";
      default:
        return "Asset";
    }
  };

  const getDebtTypeLabel = (type: Debt["type"]) => {
    switch (type) {
      case "credit-card":
        return "Credit Card";
      case "student-loan":
        return "Student Loan";
      case "mortgage":
        return "Mortgage";
      case "car-loan":
        return "Car Loan";
      case "personal-loan":
        return "Personal Loan";
      case "other":
        return "Other Debt";
      default:
        return "Debt";
    }
  };

  if (!dashboardData) {
    return (
      <div className="space-y-6">
        <div className="card">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Assets & Debts
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Add your first asset or debt to get started.
          </p>
          <div className="flex space-x-4 mt-4">
            <button
              onClick={() => {
                setModalType("asset");
                setShowAddModal(true);
              }}
              className="btn-primary">
              Add Asset
            </button>
            <button
              onClick={() => {
                setModalType("debt");
                setShowAddModal(true);
              }}
              className="btn-secondary">
              Add Debt
            </button>
          </div>
        </div>
      </div>
    );
  }

  const totalAssets =
    dashboardData.assets?.reduce(
      (sum: number, asset: Asset) => sum + (asset.balance || 0),
      0
    ) || 0;

  const totalDebts =
    dashboardData.debts?.reduce(
      (sum: number, debt: Debt) => sum + (debt.balance || 0),
      0
    ) || 0;

  const netWorth = totalAssets - totalDebts;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div></div>
        <div className="flex space-x-2">
          <button
            onClick={() => {
              setModalType("asset");
              setShowAddModal(true);
            }}
            className="btn-primary flex items-center space-x-1">
            <Plus className="w-4 h-4" />
            <span>Add Asset</span>
          </button>
          <button
            onClick={() => {
              setModalType("debt");
              setShowAddModal(true);
            }}
            className="btn-secondary flex items-center space-x-1">
            <Plus className="w-4 h-4" />
            <span>Add Debt</span>
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Total Assets
              </p>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                {formatCurrency(totalAssets)}
              </p>
            </div>
            <TrendUp className="w-8 h-8 text-green-500" weight="duotone" />
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Total Debts
              </p>
              <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                {formatCurrency(totalDebts)}
              </p>
            </div>
            <TrendDown className="w-8 h-8 text-red-500" weight="duotone" />
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Net Worth
              </p>
              <p
                className={`text-2xl font-bold ${
                  netWorth >= 0
                    ? "text-green-600 dark:text-green-400"
                    : "text-red-600 dark:text-red-400"
                }`}>
                {formatCurrency(netWorth)}
              </p>
            </div>
            <Wallet className="w-8 h-8 text-blue-500" weight="duotone" />
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Asset Count
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {dashboardData.assets?.length || 0}
              </p>
            </div>
            <Bank className="w-8 h-8 text-blue-500" weight="duotone" />
          </div>
        </div>
      </div>

      {/* Assets and Debts Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Assets Section */}
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Assets
            </h2>
            <button
              onClick={() => {
                setModalType("asset");
                setShowAddModal(true);
              }}
              className="btn-primary flex items-center space-x-1">
              <Plus className="w-4 h-4" />
              <span>Add Asset</span>
            </button>
          </div>
          {dashboardData.assets && dashboardData.assets.length > 0 ? (
            <div className="space-y-4">
              {dashboardData.assets.map((asset: Asset) => (
                <div
                  key={asset.id}
                  className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div aria-hidden="true">{getAssetIcon(asset.type)}</div>
                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-white">
                        {asset.name}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {getAssetTypeLabel(asset.type)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <p className="font-medium text-gray-900 dark:text-white">
                        {formatCurrency(asset.balance)}
                      </p>
                      {asset.interestRate && (
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          {asset.interestRate}% APY
                        </p>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(asset, "asset")}
                        className="btn-icon"
                        aria-label={`Edit ${asset.name}`}>
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(asset.id, "asset")}
                        className="btn-icon"
                        aria-label={`Delete ${asset.name}`}>
                        <Trash className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Bank className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">
                No assets added yet. Add your first asset to get started.
              </p>
            </div>
          )}
        </div>

        {/* Debts Section */}
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Debts
            </h2>
            <button
              onClick={() => {
                setModalType("debt");
                setShowAddModal(true);
              }}
              className="btn-primary flex items-center space-x-1">
              <Plus className="w-4 h-4" />
              <span>Add Debt</span>
            </button>
          </div>
          {dashboardData.debts && dashboardData.debts.length > 0 ? (
            <div className="space-y-4">
              {dashboardData.debts.map((debt: Debt) => (
                <div
                  key={debt.id}
                  className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div aria-hidden="true">{getDebtIcon(debt.type)}</div>
                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-white">
                        {debt.name}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {getDebtTypeLabel(debt.type)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <p className="font-medium text-red-600 dark:text-red-400">
                        {formatCurrency(debt.balance)}
                      </p>
                      {debt.interestRate && (
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          {debt.interestRate}% APR
                        </p>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(debt, "debt")}
                        className="btn-icon"
                        aria-label={`Edit ${debt.name}`}>
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(debt.id, "debt")}
                        className="btn-icon"
                        aria-label={`Delete ${debt.name}`}>
                        <Trash className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">
                No debts added yet. Add your first debt to get started.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div
            ref={modalRef}
            className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              {editingAsset || editingDebt
                ? `Edit ${modalType === "asset" ? "Asset" : "Debt"}`
                : `Add ${modalType === "asset" ? "Asset" : "Debt"}`}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Name
                </label>
                <input
                  type="text"
                  value={
                    modalType === "asset"
                      ? assetFormData.name
                      : debtFormData.name
                  }
                  onChange={(e) =>
                    modalType === "asset"
                      ? handleAssetFieldChange("name", e.target.value)
                      : handleDebtFieldChange("name", e.target.value)
                  }
                  className="input w-full"
                  placeholder={
                    modalType === "asset"
                      ? "e.g., Chase Savings, 401k"
                      : "e.g., Chase Credit Card, Student Loan"
                  }
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Type
                </label>
                <select
                  value={
                    modalType === "asset"
                      ? assetFormData.type
                      : debtFormData.type
                  }
                  onChange={(e) =>
                    modalType === "asset"
                      ? handleAssetFieldChange("type", e.target.value)
                      : handleDebtFieldChange("type", e.target.value)
                  }
                  className="select w-full">
                  {modalType === "asset" ? (
                    <>
                      <option value="savings">Savings Account</option>
                      <option value="checking">Checking Account</option>
                      <option value="investment">Investment Account</option>
                      <option value="retirement">Retirement Account</option>
                      <option value="ira">IRA</option>
                      <option value="portfolio">Portfolio</option>
                      <option value="crypto">Cryptocurrency</option>
                      <option value="real-estate">Real Estate</option>
                      <option value="vehicle">Vehicle</option>
                      <option value="other">Other Asset</option>
                    </>
                  ) : (
                    <>
                      <option value="credit-card">Credit Card</option>
                      <option value="student-loan">Student Loan</option>
                      <option value="mortgage">Mortgage</option>
                      <option value="car-loan">Car Loan</option>
                      <option value="personal-loan">Personal Loan</option>
                      <option value="other">Other Debt</option>
                    </>
                  )}
                </select>
              </div>

              <DollarInput
                value={
                  modalType === "asset"
                    ? assetFormData.balance
                    : debtFormData.balance
                }
                onChange={(value) =>
                  modalType === "asset"
                    ? handleAssetFieldChange("balance", value)
                    : handleDebtFieldChange("balance", value)
                }
                placeholder="$0.00"
                label="Balance"
                required
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <DollarInput
                  value={
                    modalType === "asset"
                      ? assetFormData.monthlyContribution
                      : debtFormData.monthlyPayment
                  }
                  onChange={(value) =>
                    modalType === "asset"
                      ? handleAssetFieldChange("monthlyContribution", value)
                      : handleDebtFieldChange("monthlyPayment", value)
                  }
                  placeholder="$0.00"
                  label={
                    modalType === "asset"
                      ? "Monthly Contribution"
                      : "Monthly Payment"
                  }
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Interest Rate (%)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={
                      modalType === "asset"
                        ? assetFormData.interestRate
                        : debtFormData.interestRate
                    }
                    onChange={(e) =>
                      modalType === "asset"
                        ? handleAssetFieldChange(
                            "interestRate",
                            parseFloat(e.target.value) || 0
                          )
                        : handleDebtFieldChange(
                            "interestRate",
                            parseFloat(e.target.value) || 0
                          )
                    }
                    className="input w-full"
                    placeholder="0.00"
                  />
                </div>
              </div>

              {modalType === "asset" && (
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="isLiquid"
                    checked={assetFormData.isLiquid}
                    onChange={(e) =>
                      handleAssetFieldChange("isLiquid", e.target.checked)
                    }
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <label
                    htmlFor="isLiquid"
                    className="text-sm text-gray-700 dark:text-gray-300">
                    Liquid asset (easily accessible)
                  </label>
                </div>
              )}

              {modalType === "debt" && (
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={debtFormData.isActive}
                    onChange={(e) =>
                      handleDebtFieldChange("isActive", e.target.checked)
                    }
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <label
                    htmlFor="isActive"
                    className="text-sm text-gray-700 dark:text-gray-300">
                    Active debt
                  </label>
                </div>
              )}

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="btn-secondary flex-1">
                  Cancel
                </button>
                <button type="submit" className="btn-primary flex-1">
                  {editingAsset || editingDebt ? "Update" : "Add"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Assets;
